import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getRoute,
  getMultiPointRoute,
  getDistanceMatrix,
  type RouteResponse,
  type CostingModel,
} from '@/api/geo';
import { decodePolyline } from '@/features/navigation/lib/polyline';
import { optimizeRoute, optimizeRouteLarge, canOptimizeClientSide } from '@/lib/route-optimizer';
import type { Waypoint, RoutePlannerState } from '../lib/types';
import { decodeRouteFromUrl, generateWaypointId } from '../lib/route-url';

export interface UseRoutePlannerResult {
  state: RoutePlannerState;
  actions: {
    addWaypoint: (waypoint: Omit<Waypoint, 'id'>) => void;
    removeWaypoint: (id: string) => void;
    reorderWaypoints: (waypoints: Waypoint[]) => void;
    clearWaypoints: () => void;
    setCosting: (costing: CostingModel) => void;
    optimizeOrder: () => Promise<void>;
    optimizeFromStart: () => Promise<void>;
    calculateRoute: () => Promise<void>;
    loadWaypoints: (waypoints: Waypoint[], costing?: CostingModel) => void;
  };
}

export function useRoutePlanner(): UseRoutePlannerResult {
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<RoutePlannerState>(() => {
    // Try to load from URL on init
    const urlData = decodeRouteFromUrl(searchParams);
    return {
      waypoints: urlData?.waypoints || [],
      costing: urlData?.costing || 'auto',
      routeResponse: null,
      decodedShape: [],
      isLoading: false,
      isOptimizing: false,
      error: null,
    };
  });

  // Calculate route when waypoints change (if >= 2 points)
  useEffect(() => {
    if (state.waypoints.length >= 2 && !state.isLoading && !state.isOptimizing) {
      calculateRouteInternal();
    } else if (state.waypoints.length < 2) {
      // Clear route if less than 2 points
      setState((prev) => ({
        ...prev,
        routeResponse: null,
        decodedShape: [],
        error: null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.waypoints, state.costing]);

  const calculateRouteInternal = useCallback(async () => {
    if (state.waypoints.length < 2) return;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const locations = state.waypoints.map((w) => ({ lat: w.lat, lon: w.lon }));

      let route: RouteResponse;
      if (locations.length === 2) {
        // Simple A to B route
        route = await getRoute(locations[0], locations[1], state.costing);
      } else {
        // Multi-point route via OSRM (keeps user order)
        route = await getMultiPointRoute(locations, state.costing);
      }

      // Decode the first leg's shape (OSRM returns full route geometry in each leg)
      // We only need to decode once since all legs have the same full geometry
      const decoded = route.trip.legs[0]?.shape
        ? decodePolyline(route.trip.legs[0].shape)
        : [];

      // Remove circular segment if last point is close to first (within ~100m)
      let allShapes = decoded;
      if (allShapes.length > 2) {
        const first = allShapes[0];
        const last = allShapes[allShapes.length - 1];
        const distLon = Math.abs(first[0] - last[0]);
        const distLat = Math.abs(first[1] - last[1]);
        // ~0.001 degree ≈ 100m at Belarus latitude
        if (distLon < 0.001 && distLat < 0.001) {
          // Find where the return segment starts (point closest to first after midpoint)
          const midIdx = Math.floor(allShapes.length / 2);
          let cutIdx = allShapes.length;
          for (let i = midIdx; i < allShapes.length; i++) {
            const pt = allShapes[i];
            const d = Math.abs(pt[0] - first[0]) + Math.abs(pt[1] - first[1]);
            if (d < 0.002) {
              cutIdx = i;
              break;
            }
          }
          if (cutIdx < allShapes.length) {
            allShapes = allShapes.slice(0, cutIdx);
          }
        }
      }

      setState((prev) => ({
        ...prev,
        routeResponse: route,
        decodedShape: allShapes,
        isLoading: false,
        error: null,
      }));
    } catch (err) {
      console.error('Failed to calculate route:', err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Не удалось построить маршрут',
      }));
    }
  }, [state.waypoints, state.costing]);

  const addWaypoint = useCallback((waypoint: Omit<Waypoint, 'id'>) => {
    const newWaypoint: Waypoint = {
      ...waypoint,
      id: generateWaypointId(),
    };
    setState((prev) => ({
      ...prev,
      waypoints: [...prev.waypoints, newWaypoint],
    }));
  }, []);

  const removeWaypoint = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      waypoints: prev.waypoints.filter((w) => w.id !== id),
    }));
  }, []);

  const reorderWaypoints = useCallback((waypoints: Waypoint[]) => {
    setState((prev) => ({
      ...prev,
      waypoints,
    }));
  }, []);

  const clearWaypoints = useCallback(() => {
    setState((prev) => ({
      ...prev,
      waypoints: [],
      routeResponse: null,
      decodedShape: [],
      error: null,
    }));
  }, []);

  const setCosting = useCallback((costing: CostingModel) => {
    setState((prev) => ({ ...prev, costing }));
  }, []);

  // Route optimization: client-side TSP for ≤10, Valhalla for >10
  const runOptimization = useCallback(
    async (options?: { fixStart?: boolean }) => {
      const n = state.waypoints.length;
      console.log('[OPT] runOptimization called, waypoints:', n, 'fixStart:', options?.fixStart);
      console.log('[OPT] Current waypoints:', state.waypoints.map((w, i) => `${i}: ${w.label}`));

      if (n < 3) {
        console.log('[OPT] Less than 3 waypoints, skipping');
        return;
      }

      setState((prev) => ({ ...prev, isOptimizing: true, error: null }));

      try {
        const locations = state.waypoints.map((w) => ({ lat: w.lat, lon: w.lon }));

        // Get distance matrix from Valhalla
        console.log('[OPT] Getting distance matrix...');
        const matrix = await getDistanceMatrix(locations, state.costing);

        let result;
        if (canOptimizeClientSide(n)) {
          // Brute-force for ≤10 waypoints (exact solution)
          console.log('[OPT] Using brute-force TSP (≤10 points)...');
          result = optimizeRoute(matrix.sources_to_targets, {
            fixStart: options?.fixStart ?? false,
            metric: 'time',
          });
        } else {
          // Nearest Neighbor + 2-opt for >10 waypoints (fast heuristic)
          console.log('[OPT] Using Nearest Neighbor + 2-opt (>10 points)...');
          result = optimizeRouteLarge(matrix.sources_to_targets, {
            fixStart: options?.fixStart ?? false,
            metric: 'time',
          });
        }

        console.log('[OPT] Result:', result.order, 'time:', result.totalMetric, 'iterations:', result.permutationsChecked);
        const finalOrder = result.order;

        // Reorder waypoints
        const newWaypoints = finalOrder.map((i) => state.waypoints[i]);
        console.log('[OPT] New order:', newWaypoints.map((w, i) => `${i}: ${w.label}`));

        setState((prev) => ({
          ...prev,
          waypoints: newWaypoints,
          isOptimizing: false,
          error: null,
        }));
      } catch (err) {
        console.error('Failed to optimize route:', err);
        setState((prev) => ({
          ...prev,
          isOptimizing: false,
          error: 'Не удалось оптимизировать маршрут',
        }));
      }
    },
    [state.waypoints, state.costing]
  );

  // Full TSP optimization - circular route, Valhalla optimizes all points
  const optimizeOrder = useCallback(async () => {
    await runOptimization({ fixStart: false });
  }, [runOptimization]);

  // Optimize from first point (user's location) - circular but rotate result to start from point 0
  const optimizeFromStart = useCallback(async () => {
    await runOptimization({ fixStart: true });
  }, [runOptimization]);

  const calculateRoute = useCallback(async () => {
    await calculateRouteInternal();
  }, [calculateRouteInternal]);

  const loadWaypoints = useCallback((waypoints: Waypoint[], costing?: CostingModel) => {
    setState((prev) => ({
      ...prev,
      waypoints,
      costing: costing || prev.costing,
    }));
  }, []);

  return {
    state,
    actions: {
      addWaypoint,
      removeWaypoint,
      reorderWaypoints,
      clearWaypoints,
      setCosting,
      optimizeOrder,
      optimizeFromStart,
      calculateRoute,
      loadWaypoints,
    },
  };
}
